/**
 * =============================================================================
 * WlAddonKpiGrid
 *
 * Grid de 4 KPIs premium exclusivos da aba Add-ons (WL Financeiro):
 *   1. Total Add-ons              — quantidade ativa hoje + MRR como sublinha
 *   2. Novo Add-on (mês)          — quantidade contratada no período
 *   3. Add-ons cancelados (mês)   — quantidade cancelada no período
 *   4. Ticket médio por add-on    — MRR total / add-ons ativos
 *
 * v1.1.0: cada card é clicável e abre `WlFinancialDetailModal` (type='addon')
 * com filtros de período alinhados ao KPI (mesma janela SP TZ dos KPIs).
 *
 * @version 1.1.1
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial.
 *   2026-07-22 v1.1.0 - Drill-down por card via WlFinancialDetailModal
 *                       (Total, Novo mês, Cancelados mês, Ticket médio).
 *   2026-07-22 v1.1.1 - Fix TZ: `referenceMonth` (yyyy-MM-dd) era parseado como
 *                       UTC (00:00Z), caindo no mês anterior em SP (-03:00).
 *                       Agora ancoramos ao meio-dia SP para garantir que a
 *                       janela do modal case exatamente com a janela do KPI.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, PackageX, PackagePlus, DollarSign } from 'lucide-react';
import { CrmPremiumKpiCard } from '@/components/crm/dashboard/premium';
import { useWlAddonKpis } from '@/hooks/wl/useWlAddonKpis';
import { WlFinancialDetailModal } from './WlFinancialDetailModal';
import { getMonthRangeSP } from './utils/monthRangeSP';

interface Props {
  /** Mês de referência (yyyy-MM-dd) vindo do header global; null = mês corrente. */
  referenceMonth?: string | null;
}

// Formatador BRL (pt-BR).
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(v || 0),
  );

/** Helper: junta título e contador de registros (mesmo padrão da aba Cobrança). */
const withCount = (label: string, count: number, singular = 'registro') =>
  `${label} · ${count} ${count === 1 ? singular : `${singular}s`}`;

/** Calcula progresso percentual limitado entre 0 e 100. */
const pct = (num: number, den: number) => {
  if (!den || den <= 0) return 0;
  const v = (num / den) * 100;
  return Math.max(0, Math.min(100, Math.round(v)));
};

/**
 * Estado do modal de drill-down. Somente `type='addon'` é usado nesta grid,
 * mas mantemos o shape amplo para casar com a assinatura do modal genérico.
 */
type AddonModalState = {
  open: boolean;
  title: string;
  /** Janela ISO em `activated_at` (Novo Add-on). */
  activatedFrom?: string | null;
  activatedTo?: string | null;
  /** Janela ISO em `cancelled_at` (Cancelados). */
  cancelledFrom?: string | null;
  cancelledTo?: string | null;
};

export function WlAddonKpiGrid({ referenceMonth }: Props) {
  const { data, isLoading } = useWlAddonKpis(referenceMonth ?? null);

  // Janela mensal (SP TZ) para o mês selecionado no header — MESMA usada pela
  // RPC de KPIs, garantindo paridade KPI × modal.
  const monthRange = useMemo(() => {
    // IMPORTANTE: `referenceMonth` chega como 'yyyy-MM-dd' (ex.: '2026-07-01').
    // `new Date('2026-07-01')` é interpretado como UTC 00:00Z, o que em SP
    // (-03:00) equivale a 2026-06-30 21:00 → getMonthRangeSP resolveria JUNHO.
    // Ancoramos ao meio-dia no fuso SP para garantir o mês correto.
    const anchor = referenceMonth
      ? new Date(`${referenceMonth}T12:00:00-03:00`)
      : new Date();
    return getMonthRangeSP(anchor);
  }, [referenceMonth]);

  const [modal, setModal] = useState<AddonModalState>({
    open: false,
    title: '',
  });

  /** Fecha o modal preservando os demais campos (para animação suave). */
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[130px] w-full" />
        ))}
      </div>
    );
  }

  const k = data.kpis;
  const totalActive = Number(k.total_addons?.value ?? 0);
  const totalMrr = Number(k.total_addons?.mrr ?? k.total_addons_mrr?.value ?? 0);
  const newCount = Number(k.new_addons_month?.count ?? 0);
  const cancelCount = Number(k.canceled_addons_month?.count ?? 0);
  const avgTicket = Number(k.avg_addon_ticket?.value ?? 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <CrmPremiumKpiCard
          title={withCount('Total Add-ons', totalActive, 'ativo')}
          value={fmtBRL(totalMrr)}
          icon={Package}
          variant="success"
          progress={totalMrr > 0 ? 90 : 0}
          tooltip="Quantidade de add-ons com status 'active' hoje e o MRR total resultante (soma de preço mensal × quantidade, normalizado por recorrência). Exclui tenants cortesia."
          onClick={() =>
            setModal({
              open: true,
              title: 'Add-ons ativos',
              // Snapshot: sem janelas → modal usa p_status='active' (default).
              activatedFrom: null,
              activatedTo: null,
              cancelledFrom: null,
              cancelledTo: null,
            })
          }
        />

        <CrmPremiumKpiCard
          title={withCount('Novo Add-on (mês)', newCount)}
          value={fmtBRL(k.new_addons_month?.value ?? 0)}
          icon={PackagePlus}
          variant="info"
          progress={
            totalActive > 0 ? pct(newCount, totalActive) : newCount > 0 ? 60 : 0
          }
          tooltip="Add-ons contratados dentro do período selecionado (activated_at no mês, fuso America/Sao_Paulo). Valor exibido é o MRR incremental gerado pelas contratações do mês."
          onClick={() =>
            setModal({
              open: true,
              title: `Novos add-ons — ${monthRange.label}`,
              activatedFrom: monthRange.startISO,
              activatedTo: monthRange.endISO,
              cancelledFrom: null,
              cancelledTo: null,
            })
          }
        />

        <CrmPremiumKpiCard
          title={withCount('Add-ons cancelados (mês)', cancelCount)}
          value={fmtBRL(k.canceled_addons_month?.value ?? 0)}
          icon={PackageX}
          variant="warning"
          progress={
            totalActive > 0
              ? pct(cancelCount, totalActive)
              : cancelCount > 0
                ? 40
                : 0
          }
          tooltip="Add-ons cancelados dentro do período selecionado (cancelled_at no mês, fuso America/Sao_Paulo). Valor exibido é o MRR perdido pelos cancelamentos."
          onClick={() =>
            setModal({
              open: true,
              title: `Add-ons cancelados — ${monthRange.label}`,
              activatedFrom: null,
              activatedTo: null,
              cancelledFrom: monthRange.startISO,
              cancelledTo: monthRange.endISO,
            })
          }
        />

        <CrmPremiumKpiCard
          title="Ticket médio por add-on"
          value={fmtBRL(avgTicket)}
          icon={DollarSign}
          variant="purple"
          progress={avgTicket > 0 ? 75 : 0}
          tooltip="MRR total de add-ons dividido pela quantidade de add-ons ativos. Reflete o preço médio praticado no catálogo (normalizado por recorrência)."
          onClick={() =>
            setModal({
              open: true,
              // Mesma listagem de ativos que compõe o ticket médio.
              title: 'Composição do ticket médio (add-ons ativos)',
              activatedFrom: null,
              activatedTo: null,
              cancelledFrom: null,
              cancelledTo: null,
            })
          }
        />
      </div>

      {/*
        Modal genérico de drill-down (type='addon'). Reaproveita infra existente
        das demais abas do WL Financeiro — nada novo é adicionado no shell.
      */}
      <WlFinancialDetailModal
        open={modal.open}
        onOpenChange={(open) => (open ? null : closeModal())}
        type="addon"
        title={modal.title}
        activatedFrom={modal.activatedFrom}
        activatedTo={modal.activatedTo}
        cancelledFrom={modal.cancelledFrom}
        cancelledTo={modal.cancelledTo}
      />
    </>
  );
}
